import { Layout } from "@visma-netvisor/react-component-library";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { sendPurchaseEvent, sendTag } from "../../analytics";
import { sendGAPurchase } from "../../googleAnalytics";
import { strings } from "../../constants";
import { getOrderCookieData } from "../../utils/getOrderCookieData";
import { logFrontendError } from "../LogFrontendError";
import { useCreateOrder, OrderCreationError } from "../hooks/useCreateOrder";
import CreationStatusContent from "./CreationStatusContent";
import CreationStatusHeader from "./CreationStatusHeader";

const StatusContent = () => {
  const [companyAlreadyExist, setCompanyAlreadyExist] = useState(
    sessionStorage.getItem("companyAlreadyExists") === "1"
  );
  const [activationError, setActivationError] = useState(false);
  const [orderRetryReason, setOrderRetryReason] = useState<
    "auth" | "network" | "restart" | null
  >(null);
  const [companyCreatedSuccess, setCompanyCreatedSuccess] = useState(
    sessionStorage.getItem("companyCreationSuccess") === "1"
  );
  const { encodedOrderData, decodedOrderData } = getOrderCookieData();

  const orderId = sessionStorage.getItem(strings.ORDER_ID_COOKIE_NAME) || "";
  const [companyCreationStatus, setCompanyCreationStatus] = useState<
    number | null | undefined
  >(orderId ? strings.CREATION_STATUS_ENUMS.READY_FOR_PROCESSING : null);

  const useCreateOrderMutation = useCreateOrder();
  const hasCreatedRef = useRef(false);

  const attemptKey = `orderCreateAttempt:${decodedOrderData?.OrganizationIdentifier ?? "_"}`;
  const reauthKey = `reauthAttempted:${decodedOrderData?.OrganizationIdentifier ?? "_"}`;
  const authError = new URLSearchParams(window.location.search).get("authError");

  const createCompany = useCallback(async () => {
    try {
      await useCreateOrderMutation.mutateAsync({
        order: encodedOrderData,
        setCompanyAlreadyExist: setCompanyAlreadyExist,
      });
      setCompanyCreationStatus(
        strings.CREATION_STATUS_ENUMS.READY_FOR_PROCESSING
      );
      sessionStorage.removeItem(attemptKey);
      sessionStorage.removeItem(reauthKey);
      sendTag({ status: "authenticationSuccess" });
      sendPurchaseEvent();
      sendGAPurchase();
    } catch (error) {
      const isOrderError = error instanceof OrderCreationError;
      const isNetworkError = error instanceof TypeError;
      const isAuthFailure = isOrderError && error.status === 401;
      const attempt = Number(sessionStorage.getItem(attemptKey) ?? "0") + 1;
      sessionStorage.setItem(attemptKey, String(attempt));
      const info = isAuthFailure
        ? "Order creation failed - auth failure (401)"
        : isNetworkError
          ? "Order creation failed - network blocked"
          : "Order creation failed - other";
      await logFrontendError({
        info,
        orderId,
        organizationIdentifier: decodedOrderData?.OrganizationIdentifier,
        status: isOrderError ? error.status : undefined,
        errorCode: isOrderError ? error.errorCode : undefined,
        backendStatus: isOrderError ? error.backendStatus : undefined,
        backendErrorCode: isOrderError ? error.backendErrorCode : undefined,
        error,
        details: {
          errorKind: isOrderError
            ? "OrderCreationError"
            : (error instanceof Error ? error.name : "unknown"),
          attempt,
          cookiesEnabled: navigator.cookieEnabled,
          hasAnyCookie: document.cookie.length > 0,
          online: navigator.onLine,
          globalPrivacyControl:
            (navigator as Navigator & { globalPrivacyControl?: boolean })
              .globalPrivacyControl ?? null,
          connectionEffectiveType:
            (navigator as Navigator & { connection?: { effectiveType?: string } })
              .connection?.effectiveType ?? null,
        },
      });
      const isRecoverable = isAuthFailure || isNetworkError;
      if (isRecoverable && attempt <= 1) {
        setOrderRetryReason(isAuthFailure ? "auth" : "network");
      } else {
        setActivationError(true);
      }
    }
  }, [useCreateOrderMutation, encodedOrderData]);

  useEffect(() => {
    if (hasCreatedRef.current) return;
    if (companyAlreadyExist) {
      hasCreatedRef.current = true;
      return;
    }
    if (authError === "correlation" || authError === "denied") {
      hasCreatedRef.current = true;
      if (!encodedOrderData || !decodedOrderData) {
        // Order payload lost (e.g. sessionStorage cleared during the auth round-trip): re-auth can't recover the orgId, so send the user back to start over instead of a re-auth that dead-ends at the customer-care screen.
        setOrderRetryReason("restart");
        return;
      }
      if (sessionStorage.getItem(reauthKey)) {
        sessionStorage.removeItem(reauthKey);
        setActivationError(true);
      } else {
        setOrderRetryReason("auth");
      }
      return;
    }
    if (companyCreationStatus !== null) return;
    hasCreatedRef.current = true;
    if (!encodedOrderData || !decodedOrderData) {
      const navEntry = performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined;
      let referrerOrigin = "";
      try {
        referrerOrigin = document.referrer
          ? new URL(document.referrer).origin
          : "";
      } catch {
        referrerOrigin = "";
      }
      logFrontendError({
        info: "Order creation skipped - order payload missing after auth",
        level: "warning",
        details: {
          hadSessionStorage:
            sessionStorage.getItem(strings.ORDER_DATA_COOKIE_NAME) != null,
          cookiesEnabled: navigator.cookieEnabled,
          hasAnyCookie: document.cookie.length > 0,
          online: navigator.onLine,
          navigationType: navEntry?.type ?? "unknown",
          referrerOrigin,
        },
      });
      setOrderRetryReason("restart");
      return;
    }
    createCompany();
  }, []);

  return (
    <CustomLayout>
      <GridContainer>
        <CreationStatusHeader
          isError={companyAlreadyExist || activationError}
          formFirstName={decodedOrderData?.FirstName}
        />
        <Layout.Item as="header">
          <CreationStatusContent
            tradeRegistrationYear={decodedOrderData?.TradeRegistrationYear}
            orderId={orderId}
            activationError={activationError}
            orderRetryReason={orderRetryReason}
            organizationIdentifier={decodedOrderData?.OrganizationIdentifier}
            companyAlreadyExist={companyAlreadyExist}
            companyCreatedSuccess={companyCreatedSuccess}
            setCompanyAlreadyExist={setCompanyAlreadyExist}
            setActivationError={setActivationError}
            setCompanyCreatedSuccess={setCompanyCreatedSuccess}
          />
        </Layout.Item>
      </GridContainer>
    </CustomLayout>
  );
};

const CustomLayout = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  box-sizing: border-box;
  display: flex;
  // safe + overflow-y: when the card is taller than the viewport, scroll instead of clipping the action buttons off-screen
  align-items: safe center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 16px;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const GridContainer = styled.div`
  display: grid;
  gap: 16px;
  padding: 24px;
  max-width: 1015px;
  width: 100%;
  min-height: 422px;
  background-color: #ffffff;
  border-radius: 10px;

  @media (max-width: 768px) {
    gap: 0px;
    padding: 16px;
    min-height: 350px;
  }

  @media (max-height: 480px) {
    min-height: auto;
  }
`;

export default StatusContent;
